import { useState, useEffect, useContext } from 'react';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import { ArrowLeft, MessageSquare, User, Calendar, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const AdminFeedback = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchFeedback = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/feedback`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeedback(res.data.feedback || []);
      setError(null);
    } catch (err) {
      if (err.response?.status === 403) {
        setError("Accés denegat. Només l'administrador pot veure el feedback.");
      } else {
        setError("Error carregant el feedback.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeedback();
  }, []);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('ca-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-teal-950">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Tornar</span>
          </button>
          <button
            onClick={fetchFeedback}
            className="flex items-center gap-2 text-teal-400 hover:text-teal-300 transition-colors"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            <span>Actualitzar</span>
          </button>
        </div>

        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-2">
            <MessageSquare className="w-8 h-8 text-teal-400" />
            <h1 className="text-3xl font-bold text-teal-200">Feedback</h1>
          </div>
          <p className="text-gray-400">
            {feedback.length} comentaris rebuts
          </p>
        </div>

        {error ? (
          <div className="glass rounded-2xl p-8 border border-red-400/30 text-center">
            <p className="text-red-400">{error}</p>
          </div>
        ) : loading ? (
          <div className="glass rounded-2xl p-8 border border-teal-400/20 text-center">
            <RefreshCw className="w-8 h-8 text-teal-400 animate-spin mx-auto mb-4" />
            <p className="text-gray-400">Carregant feedback...</p>
          </div>
        ) : feedback.length === 0 ? (
          <div className="glass rounded-2xl p-8 border border-teal-400/20 text-center">
            <MessageSquare className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">Encara no hi ha feedback dels usuaris.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {feedback.map((item, index) => (
              <div
                key={item.id || index}
                className="glass rounded-2xl p-6 border border-teal-400/20 hover:border-teal-400/40 transition-all"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-teal-900/50 flex items-center justify-center">
                      <User className="w-5 h-5 text-teal-400" />
                    </div>
                    <div>
                      <p className="font-semibold text-teal-200">{item.user_name}</p>
                      <p className="text-sm text-gray-500">{item.user_email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-gray-500 text-sm">
                    <Calendar className="w-4 h-4" />
                    <span>{formatDate(item.created_at)}</span>
                  </div>
                </div>
                <p className="text-gray-200 leading-relaxed whitespace-pre-wrap">
                  {item.feedback}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminFeedback;
